<script setup lang="ts">
/**
 * Drawer-container for the refunds queue (per `architecture-principles.md §20`).
 *
 * Owns the drawer chrome, the detail query (via `useRefundDetailQuery`),
 * the two action mutations (approve, reject), the corresponding
 * confirmation dialogs, and the order-detail navigation handler. The
 * queue container only needs to forward the selected row in via
 * `selectedRow` and listen for `@close`.
 */
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { routeNames } from '@app/router/routes'

import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import AppDrawer from '@uiKit/elements/Drawer.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import Avatar from '@uiKit/components/Avatar.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'

import { useAppToast } from '@shared/composables/useAppToast'
import { getIdempotencyKey } from '@shared/utils'

import RefundDetail from '../components/RefundDetail.vue'
import ApproveRefundDialog from '../components/ApproveRefundDialog.vue'
import RejectRefundDialog from '../components/RejectRefundDialog.vue'

import { useRefundDetailQuery } from '../api/queries/useRefundDetailQuery'
import { useApproveRefundMutation } from '../api/mutations/useApproveRefundMutation'
import { useRejectRefundMutation } from '../api/mutations/useRejectRefundMutation'
import { getRefundStateColor, getRefundStateLabel } from '../utils/refundStatusUtils'
import { REFUND_STATE, type RefundRow } from '../types/refundsTypes'

const props = defineProps<{
  selectedRow: RefundRow | null
}>()

const emit = defineEmits<{
  close: []
}>()

// ─── Drawer visibility + detail query ───────────────────────────────────────

const selectedId = computed(() => props.selectedRow?.id ?? '')

const idempotencyKey = ref('')
watch(
  () => props.selectedRow,
  (row) => {
    idempotencyKey.value = row ? getIdempotencyKey() : ''
  },
)

const { data: details, isLoading: detailsLoading } = useRefundDetailQuery(selectedId)

const drawerVisible = computed(() => props.selectedRow !== null)

function closeDrawer() {
  emit('close')
}

// ─── Display ────────────────────────────────────────────────────────────────

const drawerSubtitle = computed(() => props.selectedRow?.investorEmail ?? '')
const drawerInvestorName = computed(() => {
  if (details.value?.investor) {
    const first = details.value.investor.firstName ?? ''
    const last = details.value.investor.lastName ?? ''
    return `${first} ${last}`.trim() || props.selectedRow?.investorName || ''
  }
  return props.selectedRow?.investorName || ''
})

const drawerAmount = computed(() => props.selectedRow?.amount ?? details.value?.amount ?? '0')
const drawerCurrency = computed(
  () => props.selectedRow?.currencyCode ?? details.value?.currencyCode ?? '',
)

// ─── Cross-module navigation ────────────────────────────────────────────────

const router = useRouter()

function openOrderDetail(orderId: string) {
  router.push({ name: routeNames.ORDERS_ALL_DETAIL, params: { id: orderId } })
}

// ─── Mutations + dialog state ───────────────────────────────────────────────

const { addToast } = useAppToast()

const approveDialogVisible = ref(false)
const rejectDialogVisible = ref(false)

const {
  mutate: approveMutate,
  isPending: isApproving,
  error: approveError,
  reset: resetApprove,
} = useApproveRefundMutation()
const {
  mutate: rejectMutate,
  isPending: isRejecting,
  error: rejectError,
  reset: resetReject,
} = useRejectRefundMutation()

function describeError(err: unknown): string {
  if (!err) return ''
  if (err instanceof Error) return err.message
  const candidate = err as { message?: unknown; code?: unknown }
  if (typeof candidate.message === 'string') return candidate.message
  if (typeof candidate.code === 'string') return candidate.code
  return 'Something went wrong. Please try again.'
}

const approveErrorMessage = computed(() => describeError(approveError.value))
const rejectErrorMessage = computed(() => describeError(rejectError.value))

function openApprove() {
  resetApprove()
  approveDialogVisible.value = true
}
function openReject() {
  resetReject()
  rejectDialogVisible.value = true
}

function handleApproveConfirm(payload: { notes: string }) {
  if (!selectedId.value) return
  approveMutate(
    {
      id: selectedId.value,
      idempotencyKey: idempotencyKey.value,
      ...(payload.notes ? { notes: payload.notes } : {}),
    },
    {
      onSuccess: () => {
        addToast({
          type: 'success',
          title: 'Refund approved',
          message: 'Funds credited to the investor wallet.',
        })
        approveDialogVisible.value = false
        closeDrawer()
      },
    },
  )
}

function handleRejectConfirm(payload: { reason: string }) {
  if (!selectedId.value) return
  rejectMutate(
    {
      id: selectedId.value,
      reason: payload.reason,
      idempotencyKey: idempotencyKey.value,
    },
    {
      onSuccess: () => {
        addToast({
          type: 'success',
          title: 'Refund rejected',
          message: 'The refund row was marked rejected. No wallet change was made.',
        })
        rejectDialogVisible.value = false
        closeDrawer()
      },
    },
  )
}

const showActionFooter = computed(() => details.value?.refundState === REFUND_STATE.PENDING)
</script>

<template>
  <AppDrawer
    :model-value="drawerVisible"
    position="right"
    width="540px"
    :show-close-icon="false"
    :loading="detailsLoading"
    :dismissable="!isApproving && !isRejecting"
    @update:model-value="(v) => !v && closeDrawer()"
  >
    <template #header>
      <DrawerHeader
        :title="drawerInvestorName"
        :subtitle="drawerSubtitle"
        :disable-close="isApproving || isRejecting"
        @close="closeDrawer"
      >
        <template #avatar>
          <Avatar :id="selectedRow?.investorId ?? ''" :name="drawerInvestorName" />
        </template>
        <template #extras>
          <StatusBadge
            v-if="details"
            :color="getRefundStateColor(details.refundState)"
            :label="getRefundStateLabel(details.refundState)"
          />
        </template>
      </DrawerHeader>
    </template>

    <Box v-if="details" padding="4">
      <RefundDetail :details="details" @open-order="openOrderDetail" />
    </Box>

    <template v-if="details && showActionFooter" #footer>
      <Box class="drawer-footer" flex justify="end" gap="2">
        <Button
          label="Reject"
          color="danger"
          variant="outline"
          :disabled="isApproving"
          @click="openReject"
        />
        <Button label="Approve" color="primary" :disabled="isRejecting" @click="openApprove" />
      </Box>
    </template>
  </AppDrawer>

  <ApproveRefundDialog
    :visible="approveDialogVisible"
    :amount="drawerAmount"
    :currency-code="drawerCurrency"
    :investor-name="drawerInvestorName"
    :loading="isApproving"
    :api-error="approveDialogVisible ? approveErrorMessage : null"
    @update:visible="approveDialogVisible = $event"
    @confirm="handleApproveConfirm"
  />

  <RejectRefundDialog
    :visible="rejectDialogVisible"
    :amount="drawerAmount"
    :currency-code="drawerCurrency"
    :investor-name="drawerInvestorName"
    :loading="isRejecting"
    :api-error="rejectDialogVisible ? rejectErrorMessage : null"
    @update:visible="rejectDialogVisible = $event"
    @confirm="handleRejectConfirm"
  />
</template>

<style scoped>
.drawer-footer {
  width: 100%;
  padding: 0.25rem 0;
}
</style>
